import { MAX_TRACKED_JOB_ID_LENGTH } from "../trackedJobIdLimit";
import type { ActionPreview } from "@/src/features/actions/trust/contracts";

import {
  JOB_CONTINUITY_SCHEMA_VERSION,
  type ApprovalHandoffContract,
  type ArtifactDeepLinkInput,
  type ContinuityTargetKind,
  type JobContinuitySnapshot,
  type NotificationHandoffContract,
} from "./contracts";

export const ACTION_APPROVAL_TTL_MS = 24 * 60 * 60 * 1000;

export function actionApprovalExpiresAt(requestedAt: number): number {
  if (!Number.isFinite(requestedAt) || requestedAt < 0) {
    throw new ApprovalDecisionError("invalid_request", "Approval request time is invalid.", 400);
  }
  return Math.trunc(requestedAt) + ACTION_APPROVAL_TTL_MS;
}
import {
  buildApprovalHandoff,
  buildJobContinuitySnapshot,
  buildNotificationHandoff,
} from "./continuity";

const MAX_IDENTIFIER_LENGTH = 256;
const MAX_ARTIFACT_KIND_LENGTH = 128;
const MAX_ARTIFACTS = 50;
const MAX_TRAVERSAL_DEPTH = 7;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;

export const CONTINUITY_DEEP_LINK_STORAGE_KEY = "smartmanager:continuity-deep-link:v1";

type UnknownRecord = Record<string, unknown>;

export type ContinuityDeepLink = {
  kind: ContinuityTargetKind;
  id: string;
  jobId?: string;
  artifactKind?: string;
  rootPath: string;
  dashboardPath: string;
};

export type ApprovalDecision = "approve" | "reject";

export type ApprovalDecisionInput = {
  approvalId: unknown;
  decision: unknown;
  idempotencyKey: unknown;
};

export type ApprovalDecisionRecord = {
  approvalId: string;
  previewId: string;
  actionId: string;
  decision: ApprovalDecision;
  idempotencyKey: string;
  status: "recorded";
  decidedAt: number;
  decidedBy: string;
};

export type ApprovalDecisionResponse = {
  schemaVersion: typeof JOB_CONTINUITY_SCHEMA_VERSION;
  approvalId: string;
  jobId: string;
  decision: ApprovalDecision;
  status: "approved" | "rejected";
  transition: "recorded";
  duplicate: boolean;
  decidedAt: number;
  execution:
    | {
        status: "not_started";
        reason: string;
      }
    | {
        status: "accepted";
        jobId: string;
        runId: string;
      };
};

export class ApprovalDecisionError extends Error {
  constructor(
    readonly code:
      | "invalid_request"
      | "approval_not_found"
      | "approval_not_pending"
      | "approval_conflict"
      | "job_terminal",
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApprovalDecisionError";
  }
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function optionalRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null;
}

function normalizedIdentifier(value: unknown, field: string): string {
  if (typeof value !== "string") {
    throw new ApprovalDecisionError("invalid_request", `${field} must be a string.`, 400);
  }
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_IDENTIFIER_LENGTH ||
    CONTROL_CHARACTER_PATTERN.test(normalized)
  ) {
    throw new ApprovalDecisionError("invalid_request", `${field} is invalid.`, 400);
  }
  return normalized;
}

function isActionPreview(value: unknown): value is ActionPreview {
  if (!isRecord(value)) return false;
  return (
    value.schemaVersion === "1.0" &&
    typeof value.previewId === "string" &&
    typeof value.actionId === "string" &&
    typeof value.functionName === "string" &&
    isRecord(value.confirmation) &&
    isRecord(value.idempotency)
  );
}

function previewsFromJob(job: UnknownRecord): ActionPreview[] {
  const data = optionalRecord(job.data);
  if (!data) return [];

  const metadata = optionalRecord(data.metadata);
  const trustBoundary = optionalRecord(metadata?.actionTrust);
  const boundaryPreviews = Array.isArray(trustBoundary?.previews)
    ? trustBoundary.previews.filter(isActionPreview)
    : [];

  const chain = Array.isArray(data.chain) ? data.chain : [];
  const chainPreviews = chain
    .map((action) => optionalRecord(action)?.actionTrust)
    .filter(isActionPreview);
  const directPreview = isActionPreview(data.actionTrust) ? [data.actionTrust] : [];

  return Array.from(
    new Map(
      [...directPreview, ...chainPreviews, ...boundaryPreviews].map((preview) => [
        preview.previewId,
        preview,
      ]),
    ).values(),
  );
}

function previewForJob(job: UnknownRecord, approvalId?: string): ActionPreview | null {
  const previews = previewsFromJob(job);
  const data = optionalRecord(job.data);
  if (approvalId) {
    const requested = previews.find((preview) => `approval:${preview.previewId}` === approvalId) || null;
    if (!requested) return null;
    const chain = Array.isArray(data?.chain) ? data.chain : [];
    if (chain.length > 0) {
      return chain.some((action) => {
        const actionPreview = optionalRecord(action)?.actionTrust;
        return isActionPreview(actionPreview) && actionPreview.previewId === requested.previewId;
      }) ? requested : null;
    }
    const boundPreview = previewForJob(job);
    return boundPreview?.previewId === requested.previewId ? requested : null;
  }

  const chainIndex = Number(data?.chainIndex);
  if (Number.isSafeInteger(chainIndex) && chainIndex >= 0 && previews[chainIndex]) {
    return previews[chainIndex];
  }

  const chain = Array.isArray(data?.chain) ? data.chain : [];
  if (chain.length > 0) {
    return previews.find((preview) => preview.confirmation.status === "pending") || previews[0] || null;
  }

  const jobType = typeof job.type === "string" ? job.type : "";
  const matching = previews.filter((preview) => preview.functionName === jobType);
  if (matching.length === 1) return matching[0];
  return previews.length === 1 ? previews[0] : null;
}

function approvalDecisionPath(jobId: string): string {
  return `/api/action-approvals?${new URLSearchParams({ jobId }).toString()}`;
}

function decisionRecords(job: UnknownRecord): ApprovalDecisionRecord[] {
  const data = optionalRecord(job.data);
  const metadata = optionalRecord(data?.metadata);
  const records = metadata?.continuityApprovalDecisions;
  if (!Array.isArray(records)) return [];
  return records.filter((record): record is ApprovalDecisionRecord => {
    return Boolean(
      isRecord(record) &&
      typeof record.approvalId === "string" &&
      typeof record.previewId === "string" &&
      typeof record.actionId === "string" &&
      (record.decision === "approve" || record.decision === "reject") &&
      typeof record.idempotencyKey === "string" &&
      record.status === "recorded" &&
      typeof record.decidedAt === "number" &&
      typeof record.decidedBy === "string",
    );
  });
}

function approvalForJob(job: UnknownRecord, generatedAt: number): ApprovalHandoffContract | undefined {
  const jobId = typeof job.jobId === "string" ? job.jobId : "";
  if (!jobId) return undefined;
  const preview = previewForJob(job);
  if (!preview) return undefined;

  const status = String(job.status || "").toLowerCase();
  const terminal = status === "completed" || status === "failed" || status === "cancelled";
  const effectivePreview = terminal && preview.confirmation.status === "pending"
    ? {
        ...preview,
        confirmation: {
          ...preview.confirmation,
          requirement: "blocked" as const,
          status: "blocked" as const,
          allowsExecution: false,
          reason: "The job reached a terminal state before this approval was decided.",
        },
      }
    : preview;

  const requestedAt =
    typeof job.createdAt === "number" && Number.isFinite(job.createdAt)
      ? job.createdAt
      : generatedAt;
  return buildApprovalHandoff(effectivePreview, {
    decisionPath: approvalDecisionPath(jobId),
    requestedAt,
    expiresAt: actionApprovalExpiresAt(requestedAt),
    now: generatedAt,
  }) || undefined;
}

/**
 * Whether one of a job's approvals still waits on a decision. "settled" means
 * it was decided, lapsed, or its job ended; "unknown" means the job never
 * held that approval.
 */
export function approvalStateForJob(
  job: UnknownRecord,
  approvalId: string,
  now = Date.now(),
): "pending" | "settled" | "unknown" {
  if (decisionRecords(job).some((record) => record.approvalId === approvalId)) return "settled";
  const jobId = typeof job.jobId === "string" ? job.jobId : "";
  const preview = jobId ? previewForJob(job, approvalId) : null;
  if (!preview) return "unknown";
  const status = String(job.status || "").toLowerCase();
  if (status === "completed" || status === "failed" || status === "cancelled") return "settled";
  const requestedAt = typeof job.createdAt === "number" && Number.isFinite(job.createdAt) ? job.createdAt : now;
  const handoff = buildApprovalHandoff(preview, {
    decisionPath: approvalDecisionPath(jobId),
    requestedAt,
    expiresAt: actionApprovalExpiresAt(requestedAt),
    now,
  });
  return handoff?.status === "pending" && handoff.canDecide && preview.idempotency.state === "prepared"
    ? "pending"
    : "settled";
}

function artifactInputsFromJob(job: UnknownRecord): ArtifactDeepLinkInput[] {
  const artifacts = new Map<string, ArtifactDeepLinkInput>();
  const seen = new Set<object>();

  const visit = (value: unknown, depth: number) => {
    if (depth > MAX_TRAVERSAL_DEPTH || artifacts.size >= MAX_ARTIFACTS || value == null) return;
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
      return;
    }
    if (!isRecord(value) || seen.has(value)) return;
    seen.add(value);

    if (typeof value.artifactId === "string" && value.artifactId.trim()) {
      const artifactId = value.artifactId.trim();
      if (!artifacts.has(artifactId)) {
        artifacts.set(artifactId, {
          artifactId,
          kind: value.kind ?? value.type,
          title: value.title ?? value.name,
        });
      }
    }

    for (const [key, child] of Object.entries(value)) {
      if (
        key === "result" ||
        key === "components" ||
        key === "fetchedData" ||
        key === "artifacts" ||
        key === "artifact"
      ) {
        visit(child, depth + 1);
      }
    }
  };

  visit(job.result, 0);
  visit(job.components, 0);
  visit(job.artifacts, 0);
  visit(optionalRecord(job.data)?.artifacts, 0);
  return Array.from(artifacts.values());
}

function notificationForJob(job: UnknownRecord): NotificationHandoffContract | undefined {
  const data = optionalRecord(job.data);
  const metadata = optionalRecord(data?.metadata);
  const candidates = [
    job.notification,
    job.continuityHandoff,
    data?.notification,
    data?.continuityHandoff,
    metadata?.notification,
    metadata?.continuityHandoff,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      return buildNotificationHandoff(candidate as never);
    } catch {
      // A malformed optional handoff must not make the legacy job unreadable.
    }
  }
  return undefined;
}

export function buildJobStatusContinuity(
  job: UnknownRecord,
  generatedAt = Date.now(),
): JobContinuitySnapshot {
  return buildJobContinuitySnapshot(job, {
    generatedAt,
    approval: approvalForJob(job, generatedAt),
    artifacts: artifactInputsFromJob(job),
    notification: notificationForJob(job),
  });
}

export function attachJobStatusContinuity<T extends UnknownRecord>(
  job: T,
  generatedAt = Date.now(),
): T & { continuity: JobContinuitySnapshot } {
  return {
    ...job,
    continuity: buildJobStatusContinuity(job, generatedAt),
  };
}

/**
 * The recorded decision. Its execution is `not_started` here because this
 * module only records; the approval route (src/features/actions/server/routes/
 * approvals.ts) re-checks the trust gate, starts the run, and replaces it.
 */
function decisionResponse(
  record: ApprovalDecisionRecord,
  jobId: string,
  duplicate: boolean,
): ApprovalDecisionResponse {
  return {
    schemaVersion: JOB_CONTINUITY_SCHEMA_VERSION,
    approvalId: record.approvalId,
    jobId,
    decision: record.decision,
    status: record.decision === "approve" ? "approved" : "rejected",
    transition: "recorded",
    duplicate,
    decidedAt: record.decidedAt,
    execution: {
      status: "not_started",
      reason: record.decision === "approve"
        ? "The approval is recorded; the run starts once the action passes its trust check again."
        : "The action was declined, so it does not run.",
    },
  };
}

function updatePreviewConfirmation(
  preview: ActionPreview,
  decision: ApprovalDecision,
): ActionPreview {
  return {
    ...preview,
    confirmation: {
      ...preview.confirmation,
      status: decision === "approve" ? "confirmed" : "rejected",
      allowsExecution: decision === "approve",
    },
    idempotency: preview.idempotency,
  };
}

export function applyApprovalDecision(
  job: UnknownRecord,
  input: ApprovalDecisionInput,
  userId: string,
  decidedAt = Date.now(),
): { updatedData: UnknownRecord | null; response: ApprovalDecisionResponse } {
  const approvalId = normalizedIdentifier(input.approvalId, "approvalId");
  const idempotencyKey = normalizedIdentifier(input.idempotencyKey, "idempotencyKey");
  if (input.decision !== "approve" && input.decision !== "reject") {
    throw new ApprovalDecisionError("invalid_request", "decision must be approve or reject.", 400);
  }
  const decision = input.decision;
  const normalizedUserId = normalizedIdentifier(userId, "userId");
  const jobId = normalizedIdentifier(job.jobId, "jobId");
  if (!Number.isFinite(decidedAt) || decidedAt < 0) {
    throw new ApprovalDecisionError("invalid_request", "decidedAt is invalid.", 400);
  }

  const existing = decisionRecords(job).find((record) => record.approvalId === approvalId);
  if (existing) {
    if (existing.idempotencyKey !== idempotencyKey || existing.decision !== decision) {
      throw new ApprovalDecisionError(
        "approval_conflict",
        "This approval already has a different accepted decision.",
        409,
      );
    }
    return { updatedData: null, response: decisionResponse(existing, jobId, true) };
  }

  const status = String(job.status || "").toLowerCase();
  if (status === "completed" || status === "failed" || status === "cancelled") {
    throw new ApprovalDecisionError(
      "job_terminal",
      "A terminal job cannot accept a new approval decision.",
      409,
    );
  }

  const preview = previewForJob(job, approvalId);
  if (!preview) {
    throw new ApprovalDecisionError("approval_not_found", "Approval not found for this job.", 404);
  }
  const requestedAt = typeof job.createdAt === "number" && Number.isFinite(job.createdAt)
    ? job.createdAt
    : decidedAt;
  const handoff = buildApprovalHandoff(preview, {
    decisionPath: approvalDecisionPath(jobId),
    requestedAt,
    expiresAt: actionApprovalExpiresAt(requestedAt),
    now: decidedAt,
  });
  if (!handoff || handoff.status !== "pending" || !handoff.canDecide) {
    throw new ApprovalDecisionError(
      "approval_not_pending",
      "This approval is not pending a decision.",
      409,
    );
  }
  if (preview.idempotency.key !== idempotencyKey) {
    throw new ApprovalDecisionError(
      "approval_conflict",
      "The idempotency key does not match the persisted action preview.",
      409,
    );
  }
  if (preview.idempotency.state !== "prepared") {
    throw new ApprovalDecisionError(
      "approval_not_pending",
      "The action preview is no longer prepared for approval.",
      409,
    );
  }

  const data = optionalRecord(job.data) || {};
  const metadata = optionalRecord(data.metadata) || {};
  const trustBoundary = optionalRecord(metadata.actionTrust) || {};
  const updatedPreview = updatePreviewConfirmation(preview, decision);
  const replacePreview = (value: unknown) =>
    isActionPreview(value) && value.previewId === preview.previewId ? updatedPreview : value;
  const updatedPreviews = Array.isArray(trustBoundary.previews)
    ? trustBoundary.previews.map(replacePreview)
    : trustBoundary.previews;
  const updatedChain = Array.isArray(data.chain)
    ? data.chain.map((action) => {
        if (!isRecord(action)) return action;
        return { ...action, actionTrust: replacePreview(action.actionTrust) };
      })
    : data.chain;
  const record: ApprovalDecisionRecord = {
    approvalId,
    previewId: preview.previewId,
    actionId: preview.actionId,
    decision,
    idempotencyKey,
    status: "recorded",
    decidedAt: Math.trunc(decidedAt),
    decidedBy: normalizedUserId,
  };

  const updatedData: UnknownRecord = {
    ...data,
    ...(updatedChain ? { chain: updatedChain } : {}),
    ...(isActionPreview(data.actionTrust)
      ? { actionTrust: replacePreview(data.actionTrust) }
      : {}),
    metadata: {
      ...metadata,
      actionTrust: {
        ...trustBoundary,
        ...(updatedPreviews ? { previews: updatedPreviews } : {}),
      },
      continuityApprovalDecisions: [...decisionRecords(job), record],
    },
  };

  return {
    updatedData,
    response: decisionResponse(record, jobId, false),
  };
}

function safeDeepLinkIdentifier(
  value: string | null,
  maxLength = MAX_IDENTIFIER_LENGTH,
): string | null {
  if (!value) return null;
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > maxLength ||
    CONTROL_CHARACTER_PATTERN.test(normalized)
  ) {
    return null;
  }
  return normalized;
}

export function parseContinuityDeepLink(input: URLSearchParams | string): ContinuityDeepLink | null {
  const params = typeof input === "string"
    ? new URLSearchParams(input.startsWith("?") ? input.slice(1) : input)
    : input;
  const rawKind = params.get("continuity");
  if (rawKind !== "job" && rawKind !== "artifact" && rawKind !== "approval") return null;

  const idParam = rawKind === "job" ? "jobId" : rawKind === "artifact" ? "artifactId" : "approvalId";
  const id = safeDeepLinkIdentifier(params.get(idParam), rawKind === "job" ? MAX_TRACKED_JOB_ID_LENGTH : MAX_IDENTIFIER_LENGTH);
  if (!id) return null;
  const jobId = rawKind === "job" ? id : safeDeepLinkIdentifier(params.get("jobId"), MAX_TRACKED_JOB_ID_LENGTH);
  const artifactKind = rawKind === "artifact"
    ? safeDeepLinkIdentifier(params.get("artifactKind"), MAX_ARTIFACT_KIND_LENGTH)
    : null;

  const normalized = new URLSearchParams({ continuity: rawKind, [idParam]: id });
  if (artifactKind) normalized.set("artifactKind", artifactKind);
  if (jobId && rawKind !== "job") normalized.set("jobId", jobId);
  const query = normalized.toString();
  return {
    kind: rawKind,
    id,
    ...(jobId ? { jobId } : {}),
    ...(artifactKind ? { artifactKind } : {}),
    rootPath: `/?${query}`,
    dashboardPath: `/dashboard?${query}`,
  };
}
